const TRIGGER_GAP_PX = 4; // gap between the trigger and the menu
const VIEWPORT_GUTTER_PX = 8; // minimum gap kept between the menu and a reachable edge

export interface RowMenuPlacementInput {
	/** The trigger's client rect. */
	trigger: Pick<DOMRect, 'top' | 'bottom' | 'right'>;
	/** The menu's rendered (natural, uncapped) size. */
	menu: { width: number; height: number };
	/** The visible band: the parent-reported fold inside the iframe, else the window. */
	band: { top: number; bottom: number };
	/** The iframe's own box (`window.innerWidth/innerHeight`) — nothing past it can be seen or scrolled to. */
	frame: { width: number; height: number };
}

export interface RowMenuPlacement {
	top: number;
	left: number;
	/** Set only when the menu is taller than the whole reachable area — the menu then scrolls internally. */
	maxHeight?: number;
}

export const isSamePlacement = (a: RowMenuPlacement, b: RowMenuPlacement): boolean =>
	a.top === b.top && a.left === b.left && a.maxHeight === b.maxHeight;

/**
 * Places a `position: fixed` row menu so every item stays reachable: below the trigger when it fits,
 * above when it fits there, else over the trigger flush with the reachable bottom edge, and only a menu
 * taller than the whole reachable area goes on the roomier side with a height cap. The reachable area is
 * the visible band intersected with the frame, so the iframe's own bottom edge always applies.
 */
export const positionRowMenu = ({ trigger, menu, band, frame }: RowMenuPlacementInput): RowMenuPlacement => {
	const limitTop = Math.max(band.top, 0) + VIEWPORT_GUTTER_PX;
	const limitBottom = Math.min(band.bottom, frame.height) - VIEWPORT_GUTTER_PX;
	const reachableHeight = limitBottom - limitTop;
	const left = Math.max(
		VIEWPORT_GUTTER_PX,
		Math.min(trigger.right - menu.width, frame.width - menu.width - VIEWPORT_GUTTER_PX)
	);

	const belowTop = Math.max(trigger.bottom + TRIGGER_GAP_PX, limitTop);
	const aboveBottom = Math.min(trigger.top - TRIGGER_GAP_PX, limitBottom);
	const spaceBelow = Math.max(limitBottom - belowTop, 0);
	const spaceAbove = Math.max(aboveBottom - limitTop, 0);

	if (menu.height <= spaceBelow) return { top: belowTop, left };
	if (menu.height <= spaceAbove) return { top: aboveBottom - menu.height, left };
	// A height cap hides the last item behind a scrollbar, whereas covering the trigger shows every item:
	// when the reachable area holds the whole menu, place it flush with the bottom edge.
	if (menu.height <= reachableHeight) return { top: limitBottom - menu.height, left };
	if (spaceBelow >= spaceAbove) return { top: belowTop, left, maxHeight: spaceBelow };
	return { top: limitTop, left, maxHeight: spaceAbove };
};
